// CtaSection.jsx
import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { CtaSectionOuter, SectionInner, landingSpacing } from "./LandingLayout";

/* =========================
   Constantes / thème
   ========================= */
const radiusXL = "12px";
const shadow = "0 12px 30px rgba(45, 64, 89, 0.12)";
const fontStack = "'Poppins', 'Roboto', sans-serif";

/* =========================
   Styled Components
   ========================= */

// Rythme vertical + conteneur 1200px communs à toutes les sections
// (voir LandingLayout.jsx). Avant cette harmonisation, la CTA n'avait ni
// le même padding vertical que les autres sections (2.2rem/2.6rem, une
// asymétrie non intentionnelle) ni de conteneur "max-width".
const Section = styled(CtaSectionOuter)``;

const Container = styled.div`
  max-width: ${landingSpacing.container.contentMaxWidth};
  margin: 0 auto;
  border: 1px solid #d6e2ec;
  border-radius: ${radiusXL};
  box-shadow: ${shadow};
  text-align: center;
  padding: 1.5rem 1.25rem;
  background-color: #fff;

  ${props =>
    props.variant === "subtle" &&
    css`
      background: #ffffff;
      border-color: #e6eef6;
      box-shadow: 0 6px 18px rgba(45, 64, 89, 0.06);
    `}
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${fontStack};
  font-size: clamp(1.5rem, 2.6vw, 2.2rem);
  color: #1a2e40;
`;

const Subtitle = styled.p`
  margin: ${landingSpacing.gap.sm} 0 0;
  color: #4b5f73;
  font-size: 1rem;
  line-height: 1.6;
`;

const Actions = styled.div`
  margin-top: ${landingSpacing.gap.md};
  display: inline-flex;
  gap: ${landingSpacing.gap.sm};
  flex-wrap: wrap;
  justify-content: center;
`;

/* Bouton stylé réutilisable — mêmes dimensions que les boutons du Hero
   (min-height 44px, padding, arrondi) pour que les deux sections qui
   encadrent la page (Hero / CTA) partagent le même composant bouton. */
const ButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border: none;
  border-radius: 10px;
  padding: 0.62rem 1.1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: ${fontStack};
  text-decoration: none;
`;

/* Button (native) */
const Button = styled.button`
  ${ButtonBase}
  ${props =>
    props.primary
      ? css`
          background: #4caf50;
          box-shadow: 0 10px 18px #4caf5040;
          color: #fff;
          opacity: 1;
        `
      : css`
          background: #fff;
          border: 1px solid #c8d6e4;
          color: #2a4c6d;
        `}
`;

/* Link-styled (internal routes) */
const ButtonLink = styled(Link)`
  ${ButtonBase}
  ${props =>
    props.primary
      ? css`
          background: #4caf50;
          box-shadow: 0 10px 18px #4caf5040;
          color: #fff;
          opacity: 1;
        `
      : css`
          background: #fff;
          border: 1px solid #c8d6e4;
          color: #2a4c6d;
        `}
`;

/* Anchor-styled (external URLs) */
const ButtonAnchor = styled.a`
  ${ButtonBase}
  ${props =>
    props.primary
      ? css`
          background: #4caf50;
          box-shadow: 0 10px 18px #4caf5040;
          color: #fff;
          opacity: 1;
        `
      : css`
          background: #fff;
          border: 1px solid #c8d6e4;
          color: #2a4c6d;
        `}
`;

/* Helper pour détecter URL absolue */
const isAbsoluteUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

/* =========================
   Component
   ========================= */

export default function CtaSection({
  title,
  subtitle,
  actions = [],
  variant = "default" // 'default' or 'subtle'
}) {
  const safeActions = Array.isArray(actions) ? actions.filter(Boolean) : [];

  return (
    <Section className="landing-page cta-section">
      <SectionInner>
        <Container className="cta-container" variant={variant} role="region" aria-label="Call to action">
          {title && <Title>{title}</Title>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}

          <Actions className="cta">
            {safeActions.map((a, i) => {
              const {
                label,
                onClick,
                to,
                primary = false,
                ariaLabel,
                disabled = false,
                style: customStyle,
                "data-discover": dataDiscover,
                target
              } = a;

              const commonProps = {
                key: i,
                "aria-label": ariaLabel || label,
                style: customStyle,
                "data-discover": dataDiscover ?? undefined,
                primary: !!primary
              };

              // 1) URL absolue -> <a href="https://...">
              if (isAbsoluteUrl(to)) {
                return (
                  <ButtonAnchor
                    {...commonProps}
                    href={to}
                    onClick={onClick}
                    target={target || "_self"}
                    rel={target === "_blank" ? "noopener noreferrer" : undefined}
                    aria-disabled={disabled ? "true" : "false"}
                  >
                    {label}
                  </ButtonAnchor>
                );
              }

              // 2) Route interne -> <Link>
              if (typeof to === "string" && to.length > 0) {
                return (
                  <ButtonLink
                    {...commonProps}
                    to={to}
                    onClick={onClick}
                    aria-disabled={disabled ? "true" : "false"}
                  >
                    {label}
                  </ButtonLink>
                );
              }

              // 3) Pas de `to` -> bouton natif
              return (
                <Button
                  {...commonProps}
                  type="button"
                  onClick={onClick}
                  disabled={disabled}
                  aria-disabled={disabled ? "true" : "false"}
                >
                  {label}
                </Button>
              );
            })}
          </Actions>
        </Container>
      </SectionInner>
    </Section>
  );
}

/* PropTypes (optionnel) */
CtaSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
      onClick: PropTypes.func,
      primary: PropTypes.bool,
      ariaLabel: PropTypes.string,
      disabled: PropTypes.bool,
      style: PropTypes.object,
      "data-discover": PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      target: PropTypes.string
    })
  ),
  variant: PropTypes.oneOf(["default", "subtle"])
};
